import { useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  sendAnalyticsInteractionEvent,
  sendAnalyticsNonInteractionEvent,
} from "src/analytics/sendAnalyticsEvent";
import { useImpressionEvent } from "src/utils/hooks/useImpressionEvent";
import type { KoddiUrlData } from "./KoddiResponse";
import { sendKoddiClick, sendKoddiImpression } from "./api";

type UseKoddiEventsProps = {
  ref?: React.MutableRefObject<HTMLElement | null>;
  adType: "DestinationBranding" | "SponsoredHotelListing";
  data?: KoddiUrlData | null;
  shouldLogImpression: boolean;
};

/**
 * This hook is used to handle Koddi events such as impressions and clicks.
 * It will automatically track impressions when the element is
 * a minimum of 50% in the viewport for 1 second.
 * @param ref The element to observe for impression events. If ommited, the impression event can not be tracked.
 * @param adType The type of Koddi ad. Used for labeling analytics events to the specific Koddi ad type.
 * @param data The Koddi URL data, used to get the URL and event attributes for the impression and click events.
 * @param shouldLogImpression A boolean indicating if the ad's data has finished loading and is eligible to start the impression event timout.
 * @returns An object containing the sendImpressionEvent and sendClickEvent functions so that these can be accessed directly within any component.
 */
export function useKoddiEvents({
  ref,
  adType,
  data,
  shouldLogImpression,
}: UseKoddiEventsProps) {
  const hasBeenViewed = useRef(false);
  const { impressionUrl, clickTrackingUrl, urlData } = data ?? {};

  const koddiImpression = useMutation({
    mutationFn: async () => {
      if (impressionUrl && urlData && !hasBeenViewed.current) {
        return sendKoddiImpression({
          impressionUrl,
          urlData,
        });
      }
    },
    retry: 3,
  });

  const koddiClick = useMutation({
    mutationFn: async () => {
      if (clickTrackingUrl && urlData) {
        return sendKoddiClick({ clickTrackingUrl, urlData });
      }
    },
    retry: 3,
  });

  const sendImpressionEvent = () => {
    koddiImpression.mutateAsync().then((res) => {
      if (res?.ok) {
        hasBeenViewed.current = true;
        sendAnalyticsNonInteractionEvent({
          category: "Ads",
          action: "Koddi:AdViewed",
          label: adType,
        });
      }
    });
  };

  const sendClickEvent = (clickedEl?: string) => {
    koddiClick.mutateAsync().then((res) => {
      if (res?.ok) {
        // If the conditions for an impression have not
        // yet been met, then we bypass them and log
        // the impression when sending the click event.
        if (!hasBeenViewed.current && shouldLogImpression) {
          sendImpressionEvent();
        }

        sendAnalyticsInteractionEvent({
          category: "Ads",
          action: `Koddi:AdClicked`,
          label: `${adType}${clickedEl ? `:${clickedEl}` : ""}`,
        });
      }
    });
  };

  useImpressionEvent({
    ref: ref ?? { current: null },
    isInViewportCallback: sendImpressionEvent,
    isReady: shouldLogImpression,
    threshold: 0.5,
    delay: 1000,
  });

  return { sendImpressionEvent, sendClickEvent };
}
